import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Check, Sparkles, Wallet, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { normalizeObjectStrings } from '../lib/utils';
import BrandLogo from '../components/BrandLogo';
import SupportWidget from '../components/SupportWidget';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../lib/analytics';
import { getMarketingSeoConfig } from '../lib/seo';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;
const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const formatCurrency = (value) => CURRENCY_FORMATTER.format(Number(value || 0));
const formatFee = (value) => `${Number(value || 0).toFixed(2).replace('.', ',')}%`;

export default function PlansPage() {
  const [searchParams] = useSearchParams();
  const affiliateCode = searchParams.get('ref');
  const [affiliate, setAffiliate] = useState(null);
  const [apiPlans, setApiPlans] = useState({});
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [activating, setActivating] = useState(false);
  const [partnerCode, setPartnerCode] = useState('');
  const [redeemingPartnerCode, setRedeemingPartnerCode] = useState(false);

  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const seoConfig = useMemo(() => getMarketingSeoConfig('plans'), []);

  const validateAffiliate = useCallback(async () => {
    if (!affiliateCode) return;
    try {
      const response = await axios.get(`${API}/affiliate/${affiliateCode}`);
      setAffiliate(normalizeObjectStrings(response.data));
    } catch (error) {
      setAffiliate(null);
    }
  }, [affiliateCode]);

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${API}/plans`);
      setApiPlans(normalizeObjectStrings(response.data || {}));
    } catch (error) {
      setApiPlans({});
      toast.error('Não foi possível carregar as condições agora.');
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
    validateAffiliate();
  }, [loadPlans, validateAffiliate]);

  const freePlan = useMemo(() => {
    if (apiPlans?.free) return apiPlans.free;
    const firstPlanKey = Object.keys(apiPlans || {})[0];
    return firstPlanKey ? apiPlans[firstPlanKey] : null;
  }, [apiPlans]);

  const feePercentage = Number(freePlan?.fee_percentage ?? 3.59);
  const displayFeePercentage = Number(user?.fee_benefit_active ? user?.effective_fee_percentage : feePercentage);
  const isAlreadyActive = Boolean(user?.has_subscription);

  const handleActivatePlan = useCallback(async () => {
    trackEvent('plan_selected', {
      plan_id: 'free',
      plan_name: 'Plano grátis',
      affiliate_code: affiliateCode || undefined,
      authenticated: Boolean(user),
    });

    if (!user) {
      sessionStorage.setItem('selectedPlan', 'free');
      if (affiliateCode) {
        sessionStorage.setItem('affiliateCode', affiliateCode);
      }
      navigate('/register');
      return;
    }

    if (isAlreadyActive) {
      toast.info('Seu plano grátis já está ativo.');
      navigate('/dashboard');
      return;
    }

    setActivating(true);
    try {
      await axios.post(
        `${API}/subscription/activate`,
        {
          plan: 'free',
          affiliate_code: affiliateCode || null,
        },
        { withCredentials: true }
      );
      await refreshUser();
      toast.success('Plano grátis ativado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ativar plano.');
    } finally {
      setActivating(false);
    }
  }, [affiliateCode, isAlreadyActive, navigate, refreshUser, user]);

  const handleRedeemPartnerCode = useCallback(async () => {
    const normalizedCode = String(partnerCode || '').trim().toUpperCase();
    if (!normalizedCode) {
      toast.error('Digite um código de parceria para continuar.');
      return;
    }

    setRedeemingPartnerCode(true);
    try {
      const response = await axios.post(
        `${API}/partner-codes/redeem`,
        { code: normalizedCode },
        { withCredentials: true }
      );
      await refreshUser();
      setPartnerCode('');
      toast.success(response.data?.message || 'Código aplicado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível aplicar o código agora.');
    } finally {
      setRedeemingPartnerCode(false);
    }
  }, [partnerCode, refreshUser]);

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        jsonLd={seoConfig.jsonLd}
      />
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="sm" />
          </Link>

          {user ? (
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-full">Meu painel</Button>
            </Link>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#1C1C1C]">Entrar</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]">
                  Criar conta
                </Button>
              </Link>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        {affiliate && (
          <div className="mb-8 rounded-2xl border border-[#C8A97E]/30 bg-gradient-to-r from-[#C8A97E]/20 to-[#C8A97E]/10 p-5 text-center text-[#1C1C1C]">
            Você foi indicado por <span className="font-bold">{affiliate.company_name}</span> ({affiliate.category})
          </div>
        )}

        <section className="mb-8 text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#E8B7B1]/40 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#C8A97E]">
            <Sparkles className="h-3.5 w-3.5" />
            Plano único
          </p>
          <h1 className="mt-4 text-4xl text-[#1C1C1C] md:text-5xl" style={{ fontFamily: 'Playfair Display' }}>
            Comece grátis e pague só 3,59% por presente
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-[#333333]">
            Não existe assinatura mensal. O valor do presente já entra na carteira com o desconto da plataforma aplicado antes do saque.
          </p>
        </section>

        {user && (
          <section className="mb-8 rounded-2xl border border-[#E8B7B1]/35 bg-white p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Parcerias e parceiros</p>
                <h2 className="mt-2 text-2xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  {user?.fee_benefit_active ? 'Sua condição especial já está ativa' : 'Tem um código de parceria?'}
                </h2>
                <p className="mt-1 text-sm text-[#333333]">
                  {user?.fee_benefit_active
                    ? `Código ${user?.fee_benefit_code || '-'} aplicado com taxa efetiva de ${formatFee(displayFeePercentage)} por presente.`
                    : 'Aplique o código enviado por um parceiro ou campanha para personalizar a taxa da sua conta.'}
                </p>
              </div>

              {!user?.fee_benefit_active && (
                <div className="flex w-full max-w-xl flex-col gap-3 sm:flex-row">
                  <Input
                    value={partnerCode}
                    onChange={(event) => setPartnerCode(event.target.value.toUpperCase())}
                    placeholder="Digite seu código de parceria"
                    className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F6F1EF]"
                  />
                  <Button
                    onClick={handleRedeemPartnerCode}
                    disabled={redeemingPartnerCode}
                    className="h-12 rounded-xl bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]"
                  >
                    {redeemingPartnerCode ? 'Aplicando...' : 'Aplicar código'}
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="grid gap-6">
          <article className="rounded-3xl border border-[#E8B7B1]/40 bg-white p-7 shadow-[0_20px_40px_-26px_rgba(28,28,28,0.35)]">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="inline-flex rounded-full bg-[#C8A97E] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                  Menor taxa do Brasil
                </p>
                <h2 className="mt-3 text-3xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Plano grátis
                </h2>
                <p className="mt-2 text-[#333333]">
                  Publique seu site, receba presentes e acompanhe convidados em um painel único.
                </p>
              </div>

              <div className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] px-5 py-4">
                <p className="text-3xl font-semibold text-[#1C1C1C]">{formatCurrency(freePlan?.price ?? 0)}</p>
                <p className="text-sm text-[#5A4C49]">sem assinatura</p>
                <p className="mt-2 text-sm font-semibold text-[#C8A97E]">Taxa: {formatFee(displayFeePercentage)} por presente</p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {(freePlan?.features || [
                'Site personalizado',
                'Lista de presentes ilimitada',
                'Confirmação de presença e gestão de convidados',
                'Checkout para convidados com PIX e cartão',
                'Painel de recebimentos e saques',
              ]).map((feature) => (
                <div key={feature} className="flex items-start gap-2 text-sm text-[#333333]">
                  <Check className="mt-0.5 h-4 w-4 text-[#C8A97E]" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-[#5A4C49]">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E8B7B1]/40 bg-[#F6F1EF] px-3 py-1">
                <Wallet className="h-4 w-4 text-[#C8A97E]" />
                PIX
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E8B7B1]/40 bg-[#F6F1EF] px-3 py-1">
                <CreditCard className="h-4 w-4 text-[#C8A97E]" />
                Cartão
              </span>
            </div>

            <div className="mt-7">
              <Button
                onClick={handleActivatePlan}
                disabled={loadingPlans || activating}
                className="h-12 w-full rounded-xl bg-[#E8B7B1] text-base font-semibold text-white hover:bg-[#DDA39D]"
              >
                {loadingPlans
                  ? 'Carregando...'
                  : activating
                    ? 'Ativando...'
                    : isAlreadyActive
                      ? 'Ir para meu painel'
                      : 'Ativar plano grátis'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </article>

          <article className="rounded-2xl border border-[#E8B7B1]/35 bg-white px-6 py-5 text-sm text-[#333333]">
            <p className="font-semibold text-[#1C1C1C]">Como funciona a taxa de 3,59%</p>
            <p className="mt-2">
              Exemplo: em um presente de R$ 100,00, o valor creditado na carteira do casal será R$ 96,41. Assim, o saldo
              disponível já aparece líquido antes da solicitação de saque.
            </p>
          </article>
        </section>
      </main>
      <SupportWidget />
    </div>
  );
}
